import React, { useState } from 'react';
import { Star, Quote, CheckCircle2, ShieldCheck, MapPin, Globe, Award, Sparkles, Filter } from 'lucide-react';
import { testimonialsData } from '../../data/faqsData';

export const Testimonials: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<'all' | 'US' | 'UK' | 'web' | 'app'>('all');

  const filteredTestimonials = testimonialsData.filter((item) => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'US') return item.country === 'US';
    if (activeFilter === 'UK') return item.country === 'UK';
    if (activeFilter === 'web') return item.serviceCategory === 'Website Development';
    if (activeFilter === 'app') return item.serviceCategory === 'Mobile App Services';
    return true;
  });

  return (
    <section className="bg-[#050A13] py-20 border-b border-[#1b2f4a] relative overflow-hidden" id="testimonials-section">
      {/* Background ambient glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-[#159DE8]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Heading */}
        <div className="text-center max-w-3xl mx-auto mb-10 space-y-3">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0B2342] border border-[#1b2f4a] text-xs font-semibold uppercase tracking-wider text-[#FF851B]">
            <Globe size={13} className="text-[#159DE8]" />
            <span>CLIENT REVIEWS & TESTIMONIALS</span>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white font-display tracking-tight">
            Trusted by Business Leaders for Website & Mobile App Development
          </h2>

          <p className="text-[#D5DFEC] text-sm sm:text-base leading-relaxed">
            Discover how our custom web development and mobile application engineering deliver industry-leading performance, engagement, and measurable business growth.
          </p>
        </div>

        {/* Filter Pills */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
          {[
            { id: 'all', label: 'All Reviews' },
            { id: 'web', label: '💻 Website Development' },
            { id: 'app', label: '📱 Mobile App Services' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveFilter(tab.id as any)}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 border ${
                activeFilter === tab.id
                  ? 'bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white border-transparent shadow-md'
                  : 'bg-[#0c1a2d] text-[#8FA0B8] border-[#1b2f4a] hover:text-white hover:border-[#159DE8]/40'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Testimonials Grid - Limited to First 3 for a Shorter, Focused Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTestimonials.slice(0, 3).map((test) => (
            <div
              key={test.id}
              className="p-7 rounded-2xl bg-[#0c1a2d] border border-[#1b2f4a] flex flex-col justify-between hover:border-[#159DE8]/60 transition-all duration-300 shadow-card-dark group relative"
            >
              <div>
                {/* Header: Category Badge & Quote Icon */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] font-mono px-2.5 py-1 rounded bg-[#071426] text-[#159DE8] border border-[#1b2f4a] font-medium">
                    {test.serviceCategory}
                  </span>
                  <Quote size={20} className="text-[#159DE8]/40 group-hover:text-[#FF851B] transition-colors" />
                </div>

                {/* Rating Stars with Natural Variation */}
                <div className="flex items-center gap-1 text-[#FF851B] mb-4">
                  {[1, 2, 3, 4, 5].map((starIndex) => {
                    const filled = starIndex <= Math.floor(test.rating);
                    return (
                      <Star
                        key={starIndex}
                        size={15}
                        className={filled ? "fill-[#FF851B]" : "text-[#FF851B]/40"}
                      />
                    );
                  })}
                  <span className="text-xs text-[#8FA0B8] ml-1.5 font-medium">
                    {test.rating.toFixed(1)} / 5.0
                  </span>
                </div>

                {/* Quote Text */}
                <p className="text-xs sm:text-sm text-[#D5DFEC] leading-relaxed italic mb-6">
                  "{test.quote}"
                </p>
              </div>

              {/* Card Footer: Client Profile */}
              <div className="pt-4 border-t border-[#1b2f4a]/70">
                <div>
                  <div className="font-bold text-white text-sm">
                    {test.clientName}
                  </div>
                  <div className="text-xs text-[#8FA0B8] font-medium mt-0.5">
                    {test.role}
                  </div>
                  <div className="text-xs text-white/90 font-semibold mt-0.5">
                    {test.company}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Trust Indicators */}
        <div className="mt-14 p-6 rounded-2xl bg-[#0c1a2d]/60 border border-[#1b2f4a] flex flex-wrap items-center justify-around gap-6 text-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#159DE8]/10 text-[#159DE8] flex items-center justify-center font-bold">
              100%
            </div>
            <div className="text-left">
              <div className="text-xs font-bold text-white">Dedicated Project Delivery</div>
              <div className="text-[11px] text-[#8FA0B8]">Transparent weekly sprints & code reviews</div>
            </div>
          </div>

          <div className="h-8 w-px bg-[#1b2f4a] hidden sm:block" />

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#159DE8]/10 text-[#159DE8] flex items-center justify-center font-bold">
              4.9★
            </div>
            <div className="text-left">
              <div className="text-xs font-bold text-white">Client Satisfaction Rating</div>
              <div className="text-[11px] text-[#8FA0B8]">Across custom web & mobile app deployments</div>
            </div>
          </div>

          <div className="h-8 w-px bg-[#1b2f4a] hidden sm:block" />

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FF851B]/10 text-[#FF851B] flex items-center justify-center font-bold">
              NDA
            </div>
            <div className="text-left">
              <div className="text-xs font-bold text-white">Confidentiality Assured</div>
              <div className="text-[11px] text-[#8FA0B8]">Enterprise-grade IP protection on all code</div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};
